// @system — Stripe checkout + billing portal API calls
import { api } from '../../lib/@system/api.js'

// ── Types ─────────────────────────────────────────────────────────────────────
// Mirrors server/src/api/@system/stripe/index.js + subscriptions/index.js.

/** @typedef {{ reason?: string, feedback?: string }} CancelSubscriptionInput */

/**
 * `POST /stripe/cancel-subscription` / `uncancel-subscription` body.
 * @typedef {Object} CancellationResult
 * @property {string} message
 * @property {boolean} cancel_at_period_end
 */

/** @typedef {{ url: string }} CheckoutSessionResponse */

/** @typedef {{ plans: import('./payments.js').PaymentPlan[] }} StripePlansResponse */

/** @typedef {{ subscription: import('./index.js').SubscriptionRow | null }} MySubscriptionResponse */

/**
 * Fetch available pricing plans from Stripe
 * @returns {Promise<StripePlansResponse>}
 */
export async function getPlans() {
  return api.get('/subscriptions/plans')
}

/**
 * Get current user's active subscription
 * @returns {Promise<MySubscriptionResponse>}
 */
export async function getMySubscription() {
  return api.get('/subscriptions/me')
}

/**
 * Redirect to Stripe Checkout for a given priceId
 * @param {string} priceId
 * @param {number} [trialDays]
 * @returns {Promise<void>}
 */
export async function createCheckoutSession(priceId, trialDays) {
  /** @type {{ priceId: string, trialDays?: number }} */
  const body = { priceId }
  if (trialDays) body.trialDays = trialDays

  /** @type {CheckoutSessionResponse} */
  const { url } = await api.post('/stripe/create-checkout-session', body)
  if (url) window.location.href = url
}

/**
 * Redirect to Stripe Customer Portal for subscription management
 * @returns {Promise<void>}
 */
export async function createPortalSession() {
  /** @type {CheckoutSessionResponse} */
  const { url } = await api.post('/stripe/create-portal-session', {})
  if (url) window.location.href = url
}

/**
 * Cancel subscription at period end.
 * Optionally accepts a reason + free-text feedback so the server can record why
 * the user is churning (stored in the subscription metadata on the backend).
 * @param {CancelSubscriptionInput} [input]
 * @returns {Promise<CancellationResult>}
 */
export async function cancelSubscription({ reason, feedback } = {}) {
  /** @type {CancelSubscriptionInput} */
  const body = {}
  if (reason) body.reason = reason
  if (feedback) body.feedback = feedback
  return api.post('/stripe/cancel-subscription', body)
}

/**
 * Reverse a scheduled cancellation
 * @returns {Promise<CancellationResult>}
 */
export async function uncancelSubscription() {
  return api.post('/stripe/uncancel-subscription', {})
}

/**
 * Format a Stripe amount (cents) to a human-readable string
 * @param {number} amount
 * @param {string} currency
 */
export function formatAmount(amount, currency) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2 }).format(amount / 100)
}

/**
 * Format billing interval to human-readable label (e.g. "/month", "/year")
 * @param {string} interval
 * @param {number} intervalCount
 */
export function formatInterval(interval, intervalCount) {
  if (interval === 'one_time') return 'one-time'
  const label = intervalCount === 1 ? interval : `${intervalCount} ${interval}s`
  return `/${label}`
}
